import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addCart, delCart, setCart } from "../redux/action";
import { motion, AnimatePresence } from "framer-motion";
import Skeleton from "react-loading-skeleton";
import { Footer, Navbar } from "../components";
import { useAuth } from "../context/AuthContext";
import api from "../utils/base_url";
import toast from "react-hot-toast";
import StockNotifyModal from "../components/StockNotifyModal";
import { toComboCartItem, getCartQtyFor, toOrderLine, mapReservedToCartItems } from "../redux/reducer/Carthelpers";
import "../styles/index.css";
import "../styles/Combo.css";

// NOTE: adjust to your actual combo-detail endpoint if it differs.
const comboDetailUrl = (id) => `combolist/${id}/`;

const AUTO_ROTATE_MS = 3000;

const Combo = () => {
  const { id } = useParams();
  const [combo, setCombo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stockError, setStockError] = useState({});
  const [disabledQty, setDisabledQty] = useState({});

  const [notifyOpen, setNotifyOpen] = useState(false);
  const [showDescModal, setShowDescModal] = useState(false);
  const [notifyForm, setNotifyForm] = useState({
    user_id: "",
    product_id: "",
    email: "",
    quantity: 1,
  });

  // ── Carousel state ───────────────────────────────────────────
  const [images, setImages] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const touchStartX = useRef(null);
  const rotateTimer = useRef(null);

  const navigate = useNavigate();
  const { user } = useAuth();
  const state = useSelector((state) => state.handleCart);
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchCombo = async () => {
      setLoading(true);
      try {
        const res = await api.get(comboDetailUrl(id));
        const data = res.data?.data;
        setCombo(data);

        // combo image first, then each product image (deduplicated)
        const productImages = data?.images || [];
        const gallery = [data?.combo_image, ...productImages].filter(
          (img, idx, arr) => !!img && arr.indexOf(img) === idx
        );
        setImages(gallery);
        setActiveIndex(0);
      } catch (error) {
        console.error("Error fetching combo:", error);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchCombo();
  }, [id]);

  // ── Auto-rotate every 3s, pauses on hover ────────────────────
  useEffect(() => {
    if (images.length <= 1 || isPaused) return;

    rotateTimer.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % images.length);
    }, AUTO_ROTATE_MS);

    return () => clearInterval(rotateTimer.current);
  }, [images, isPaused]);

  const goToIndex = useCallback((idx) => {
    setActiveIndex(idx);
  }, []);

  const goPrev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  const goNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    const SWIPE_THRESHOLD = 40;

    if (deltaX > SWIPE_THRESHOLD) goPrev();
    else if (deltaX < -SWIPE_THRESHOLD) goNext();

    touchStartX.current = null;
  };

  // ── Cart quantity is looked up by the namespaced "combo-<id>" key, via
  // the SAME shared helper used on the Combos list page — this is what
  // keeps "already in cart" state consistent between the two pages.
  const getCartQty = (comboId) => getCartQtyFor(state, "combo", comboId);

  const addItem = () => {
    const nextQty = getCartQty(combo.id) + 1;
    if (nextQty > combo.available_stock) {
      setStockError((prev) => ({ ...prev, [combo.id]: true }));
      setDisabledQty((prev) => ({ ...prev, [combo.id]: true }));
      setTimeout(() => {
        setStockError((prev) => ({ ...prev, [combo.id]: false }));
        setDisabledQty((prev) => ({ ...prev, [combo.id]: false }));
      }, 2000);
      return;
    }
    dispatch(addCart(toComboCartItem(combo)));
  };

  const removeItem = () => dispatch(delCart(toComboCartItem(combo)));

  const initiateCheckout = async () => {
    const token = sessionStorage.getItem("access");
    if (!token) {
      sessionStorage.setItem("redirect_toast", "Please login first to continue checkout");
      sessionStorage.setItem("login_redirect", JSON.stringify({ pathname: `/combo/${id}` }));
      return navigate("/login");
    }

    const cartPayload = state.map(toOrderLine);

    try {
      const res = await api.post("checkout-initiate/", {
        user_id: user.id,
        cart: cartPayload,
      });

      if (!res.data.status) {
        toast.error(res.data.message || "Cart updated due to stock change. Please review and click checkout again.");
        dispatch(setCart(mapReservedToCartItems(res.data.reserved_items)));
        return;
      }

      navigate("/checkout");
    } catch (err) {
      console.error("initiateCheckout error:", err);
      toast.error("Unable to start checkout");
    }
  };

  const LoadingCombo = () => (
    <div className="container my-5 py-2">
      <div className="row">
        <div className="col-md-6 py-3">
          <Skeleton height={350} width={350} />
        </div>
        <div className="col-md-6 py-5">
          <Skeleton height={25} width={200} />
          <Skeleton height={80} />
          <Skeleton height={35} width={70} />
          <Skeleton height={40} width={110} />
          <Skeleton height={100} />
        </div>
      </div>
    </div>
  );

  const ShowCombo = () => {
    if (!combo) return null;

    const outOfStock = combo.available_stock === 0;
    const stockLow = combo.available_stock <= 10 && combo.available_stock > 0;
    const hasSaving =
      combo.original_price && Number(combo.original_price) > Number(combo.combo_price);

    return (
      <div className="container mt-3 mb-2 py-2">
        <div className="row align-items-center">
          {/* ── Image carousel ── */}
          <div className="col-md-6 col-sm-12 py-3 text-center">
            <div
              className="combo-image-wrapper"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              {stockError[combo.id] && (
                <motion.div
                  initial={{ x: 80, opacity: 0 }}
                  animate={{ x: [80, 0, -6, 6, -6, 6, 0], opacity: 1 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  className="stock-error-badge"
                >
                  Stock limit reached!
                </motion.div>
              )}

              <AnimatePresence mode="wait">
                <motion.img
                  key={images[activeIndex]}
                  src={images[activeIndex]}
                  alt={combo.combo_name}
                  className="combo-main-image"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.35 }}
                />
              </AnimatePresence>

              {images.length > 1 && (
                <>
                  <button
                    className="combo-carousel-arrow combo-carousel-arrow-left"
                    onClick={goPrev}
                    aria-label="Previous image"
                  >
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  <button
                    className="combo-carousel-arrow combo-carousel-arrow-right"
                    onClick={goNext}
                    aria-label="Next image"
                  >
                    <i className="fas fa-chevron-right"></i>
                  </button>

                  <div className="combo-carousel-dots">
                    {images.map((_, idx) => (
                      <span
                        key={idx}
                        className={`combo-carousel-dot ${idx === activeIndex ? "active" : ""}`}
                        onClick={() => goToIndex(idx)}
                      />
                    ))}
                  </div>
                </>
              )}

              {outOfStock && <span className="stock-badge out-stock">Out of Stock</span>}
              {!outOfStock && stockLow && (
                <span className="stock-badge low-stock">
                  Hurry! Only {combo.available_stock} left
                </span>
              )}
            </div>
          </div>

          {/* ── Info column ── */}
          <div className="col-md-6 py-3">
            <h4 className="combo-category-label">Combo</h4>
            <h1 className="combo-name">{combo.combo_name}</h1>

            <h4 className="combo-price-block">
              {hasSaving ? (
                <>
                  ₹{parseFloat(combo.combo_price).toLocaleString()}{" "}
                  <small className="text-danger text-decoration-line-through">
                    ₹{parseFloat(combo.original_price).toLocaleString()}
                  </small>
                </>
              ) : (
                `₹${parseFloat(combo.combo_price).toLocaleString()}`
              )}
            </h4>

            {/* ── Included items ── */}
            {combo.items?.length > 0 && (
              <div className="combo-items-section mt-4">
                <h6 className="fw-bold mb-3">What's included:</h6>
                <div className="combo-items-list">
                  {combo.items.map((item) => (
                    <div key={item.id} className="combo-item-row">
                      <img
                        src={item.product_image}
                        alt={item.product_name}
                        className="combo-item-thumb"
                      />
                      <div className="combo-item-info">
                        <span className="combo-item-name">{item.product_name}</span>
                        <span className="combo-item-meta">
                          Qty: {item.quantity} · ₹{parseFloat(item.product_price).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Action buttons ── */}
            <div className="d-flex flex-wrap mt-3">
              <div className="combo-btns" onClick={(e) => e.stopPropagation()}>
                {outOfStock ? (
                  <div className="d-flex flex-column gap-2">
                    <span className="out-stock-label">Out of Stock</span>
                    {/* <button
                      className="btn-notify-main"
                      onClick={() => {
                        setNotifyForm({
                          user_id: user?.id ?? "",
                          product_id: combo.id,
                          email: user?.email ?? "",
                          quantity: 1,
                        });
                        setNotifyOpen(true);
                      }}
                    >
                      🔔 Notify me when available
                    </button> */}
                  </div>
                ) : getCartQty(combo.id) === 0 ? (
                  <button className="btn-green" onClick={addItem}>
                    Add to Cart
                  </button>
                ) : (
                  <div className="qty-box">
                    <button className="qty-btn" onClick={removeItem}>
                      <i className="fas fa-minus"></i>
                    </button>
                    <span className="qty-value">{getCartQty(combo.id)}</span>
                    <button
                      className="qty-btn"
                      onClick={addItem}
                      disabled={disabledQty[combo.id]}
                    >
                      <i className="fas fa-plus"></i>
                    </button>
                  </div>
                )}

                {!outOfStock && (
                  <button
                    className="btn-checkout mb-2"
                    onClick={initiateCheckout}
                    disabled={getCartQty(combo.id) === 0}
                  >
                    Go to checkout
                  </button>
                )}
              </div>
            </div>
              <div className="mt-3">
              <p className="combo-desc-text">
                {showDescModal
                  ? combo.description
                  : `${combo.description?.slice(0, 250)}...`}
              </p>

              {combo.description?.length > 250 && (
                <button
                  type="button"
                  onClick={() => setShowDescModal(true)}
                  className="read-more-link"
                >
                  Read more
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Navbar />
      <div className="combo-page-bg">
        {loading || !combo ? <LoadingCombo /> : <ShowCombo />}
      </div>
      <Footer />

      <StockNotifyModal
        open={notifyOpen}
        onClose={() => setNotifyOpen(false)}
        product={combo ? { id: combo.id, product_name: combo.combo_name } : null}
        notifyForm={notifyForm}
        setNotifyForm={setNotifyForm}
        navigate={navigate}
      />
         {showDescModal && (
        <div className="desc-modal">
          <div className="desc-modal-content p-4 rounded shadow">
            <button className="modal-close-btn" onClick={() => setShowDescModal(false)}>×</button>
            <h5 className="desc-modal-title">Combo Description</h5>
            <div className="desc-text" >
              {combo.description}
            </div>
            <div className="modal-footer justify-content-center">
              <button
                className="btn-green"
                onClick={() => setShowDescModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Combo;